import React, { useEffect, useState } from 'react';
import { Card, Space, Button, message, Tooltip, Tag } from 'antd';
import { FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
import { WebApiMemberDtoMemberDto, WebApiModelsExternalPayment } from '../../Repository/eventbookyapi';
import { MemberService } from '../../Services/MemberService';
import { IntegrationService } from '../../Services/IntegrationService';
import { IntegrationType } from '../../Types/IntegrationType';
import { TextService } from '../../Services/TextService';
import { formatDateString } from '../../Helpers/DateHelpers';

interface MemberPaymentDocumentsProps {
    member: WebApiMemberDtoMemberDto;
}

const MemberPaymentDocuments: React.FC<MemberPaymentDocumentsProps> = ({ member }) => {
    const GetText = TextService.GetText;
    const [externalPayments, setExternalPayments] = useState<WebApiModelsExternalPayment[]>([]);
    const [loading, setLoading] = useState(true);
    const [downloadingInvoices, setDownloadingInvoices] = useState<Set<string>>(new Set());

    useEffect(() => {
        loadPayments();
    }, [member.memberId]);

    const loadPayments = async () => {
        if (!member.memberId) return;

        try {
            setLoading(true);
            const result = await MemberService.GetPayments(member.memberId);
            setExternalPayments(result.externalPayments || []);
        } catch (error) {
            console.error('Error loading payments:', error);
            message.error(GetText("AddPayment_ErrorLoading"));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async (payment: WebApiModelsExternalPayment) => {
        if (!payment.documentId) {
            message.warning(GetText("MemberPaymentDocuments_NoDocumentId"));
            return;
        }

        const invoiceKey = `${payment.integrationType}_${payment.externalId}`;
        setDownloadingInvoices(prev => new Set(prev).add(invoiceKey));

        try {
            const data = await IntegrationService.GetInvoicePdf(
                payment.integrationType as IntegrationType,
                payment.documentId!
            );

            // Create blob and download
            const url = window.URL.createObjectURL(await data.raw.blob());
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Invoice_${payment.externalId}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success(GetText("MemberPaymentDocuments_PdfDownloaded"));
        } catch (error) {
            console.error('Error downloading PDF:', error);
            message.error(GetText("MemberPaymentDocuments_PdfDownloadFailed"));
        } finally {
            setDownloadingInvoices(prev => {
                const newSet = new Set(prev);
                newSet.delete(invoiceKey);
                return newSet;
            });
        }
    };

    // Filter payments that have Fortnox integration (only Fortnox supports PDF invoices)
    const paymentsWithPdf = externalPayments.filter(
        payment => payment.integrationType === IntegrationType.Fortnox && payment.documentId
    );

    if (loading) {
        return (
            <Card size="small" title={GetText("AddPayment_FortnoxInvoices")} loading={true}>
            </Card>
        );
    }

    if (paymentsWithPdf.length === 0) {
        return null; // Don't show the card if there are no documents
    }

    return (
        <Card
            size="small"
            title={
                <>
                    <FilePdfOutlined /> {GetText("AddPayment_FortnoxInvoices")}
                </>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }} size="small">
                {paymentsWithPdf.map((payment, index) => {
                    const invoiceKey = `${payment.integrationType}_${payment.externalId}`;
                    const isDownloading = downloadingInvoices.has(invoiceKey);
                    const isPaid = payment.payDate != null;

                    return (
                        <div key={index} style={{ padding: '8px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <strong>{GetText("AddPayment_Invoice")} #{payment.documentId}</strong>
                                        {' '}
                                        {isPaid ? (
                                            <Tag color="green">{GetText("MemberList_Paid")}</Tag>
                                        ) : (
                                            <Tag color="red">{GetText("MemberList_Unpaid")}</Tag>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        <div><strong>{GetText("MemberPaymentDocuments_Paid")}:</strong> {payment.payDate ? formatDateString(payment.payDate) : '-'}</div>
                                        <div><strong>{GetText("MemberPaymentDocuments_LastPaymentDate")}:</strong> {payment.dueDate ? formatDateString(payment.dueDate) : '-'}</div>
                                        <div><strong>{GetText("MemberPaymentDocuments_InvoiceDate")}:</strong> {formatDateString(payment.invoiceDate)}</div>
                                        <div><strong>{GetText("MemberPaymentDocuments_Amount")}:</strong> {payment.totalAmount} {GetText("Common_CurrencySymbol")}</div>
                                        <div><strong>{GetText("MemberPaymentDocuments_OCR")}:</strong> {payment.externalId}</div>
                                    </div>
                                </div>
                                <Tooltip title={GetText("MemberPaymentDocuments_DownloadPdf")}>
                                    <Button
                                        type="text"
                                        icon={isDownloading ? <LoadingOutlined /> : <FilePdfOutlined />}
                                        onClick={() => handleDownloadPdf(payment)}
                                        disabled={isDownloading}
                                        style={{ color: '#1890ff', fontSize: '18px' }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    );
                })}
            </Space>
        </Card>
    );
};

export default MemberPaymentDocuments;
